"use client";

import {
  Suspense,
  useState,
} from 'react';

import { Check, Type, Image as ImageIcon, Mic, Video } from 'lucide-react';
import { useTranslations, useLocale } from 'next-intl';

import { getPricingProductionCopy } from '@/content/seo/pricing-copy';
import {
  estimatePlanVideoOutput,
  estimateVideoCostProfiles,
  type PublicPricingUnitCosts,
} from '@/libs/pricing';
import { cn } from '@/utils/utils';

import ErrorBoundary from './ErrorBoundary';
import PricingCard from './PricingCard';

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  price: number;
  billing_period: string;
  credits_included: number;
  is_featured: boolean | null;
  stripe_price_id: string | null;
  lemonsqueezy_variant_id: string | null;
  status: string;
  sort_order: number | null;
  created_at: string;
  updated_at: string;
  features?: string[] | null;
}

interface CreditPackage {
  id: string;
  name: string;
  description: string | null;
  credits_amount: number;
  price: number;
  currency: string;
  is_popular: boolean | null;
  stripe_price_id: string | null;
  lemonsqueezy_variant_id: string | null;
  status: string;
}

export default function Pricing({
  plans,
  packages = [],
  unitCosts,
}: {
  plans: SubscriptionPlan[];
  packages?: CreditPackage[];
  unitCosts: PublicPricingUnitCosts;
}) {
  const t = useTranslations('Pricing');
  const tCard = useTranslations('PricingCard');
  const tPage = useTranslations('Pricing.page');
  const locale = useLocale();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');
  const [pricingType, setPricingType] = useState<'subscriptions' | 'packages'>('subscriptions');
  const productionCopy = getPricingProductionCopy(locale);
  const profiles = estimateVideoCostProfiles(unitCosts);

  const displayedPlans = plans.filter(plan => plan.billing_period === billingCycle);

  // 根据计划类型生成本地化的 features
  const generateFeatures = (planId: string, credits: number) => {
    let key: 'basic' | 'standard' | 'pro' | null = null;
    if (planId.toLowerCase().includes('basic')) key = 'basic';
    else if (planId.toLowerCase().includes('standard')) key = 'standard';
    else if (planId.toLowerCase().includes('pro')) key = 'pro';

    if (key) {
      return [
        { name: tCard(`plans.${key}.features.f1`, { credits }) },
        { name: tCard(`plans.${key}.features.f2`) },
        { name: tCard(`plans.${key}.features.f3`) },
        { name: tCard(`plans.${key}.features.f4`) },
        { name: tCard(`plans.${key}.features.f5`) }
      ];
    }
    return [];
  };

  const getPlanDesc = (planId: string) => {
    let key: 'basic' | 'standard' | 'pro' | null = null;
    if (planId.toLowerCase().includes('basic')) key = 'basic';
    else if (planId.toLowerCase().includes('standard')) key = 'standard';
    else if (planId.toLowerCase().includes('pro')) key = 'pro';

    if (key) {
      return tCard(`plans.${key}.description`);
    }
    return '';
  };

  return (
    <section className="relative py-0 overflow-hidden" id="pricing">
      {/* Enhanced decorative background */}
      <div className="absolute inset-0 bg-gradient-to-b from-base-200/30 via-base-100 to-base-200/20" />
      <div className="absolute inset-0 bg-[url('/images/grid.svg')] bg-center opacity-30 [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />
      <div className="absolute -top-48 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl animate-pulse" />
      <div className="absolute -bottom-48 left-0 w-[500px] h-[500px] bg-secondary/5 rounded-full blur-3xl animate-pulse" />

      <div className="relative py-14 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-20 space-y-6">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold">
            <span className="relative">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-secondary to-accent">
                {tPage('title')}
              </span>
            </span>
          </h1>
          <p className="text-xl text-base-content/70 max-w-2xl mx-auto leading-relaxed">
            {tPage('subtitle')}
          </p>

          {/* Pricing type toggle */}
          <div className="flex justify-center mt-3 mb-8">
            <div className="flex p-1 bg-gray-100/80 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl">
              <button
                onClick={() => setPricingType('subscriptions')}
                className={cn(
                  "px-6 py-2 text-sm font-semibold rounded-lg transition-all",
                  pricingType === 'subscriptions'
                    ? "bg-white dark:bg-gray-700 text-primary shadow-sm"
                    : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                )}
              >
                {tPage('billingCycle.subscriptions')}
              </button>
              <button
                onClick={() => setPricingType('packages')}
                className={cn(
                  "px-6 py-2 text-sm font-semibold rounded-lg transition-all",
                  pricingType === 'packages'
                    ? "bg-white dark:bg-gray-700 text-primary shadow-sm"
                    : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                )}
              >
                {tPage('billingCycle.packages')}
              </button>
            </div>
          </div>

          {/* Billing cycle toggle (only for subscriptions) */}
          {pricingType === 'subscriptions' && (
            <div className="flex justify-center mt-4">
              <div className="relative inline-flex items-center bg-gray-100/80 dark:bg-gray-800/50 backdrop-blur-sm p-1.5 rounded-full shadow-[0_2px_8px_-1px_rgba(0,0,0,0.1)] dark:shadow-[0_2px_8px_-1px_rgba(0,0,0,0.3)]">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={cn(
                    "relative px-6 py-2 text-sm font-medium rounded-full transition-all duration-200",
                    billingCycle === 'monthly'
                      ? "bg-white text-gray-900 dark:text-white shadow-[0_2px_8px_-2px_rgba(0,0,0,0.12)]"
                      : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                  )}
                >
                  {tPage('billingCycle.monthly')}
                </button>
                <button
                  onClick={() => setBillingCycle('yearly')}
                  className={cn(
                    "relative px-6 py-2 text-sm font-medium rounded-full transition-all duration-200 flex items-center gap-2",
                    billingCycle === 'yearly'
                      ? "bg-white text-gray-900 dark:text-white shadow-[0_2px_8px_-2px_rgba(0,0,0,0.12)]"
                      : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                  )}
                >
                  <span>{tPage('billingCycle.yearly')}</span>
                  <span className="bg-rose-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                    {tPage('billingCycle.savePercent')}
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>

        <ErrorBoundary fallback={<p className="text-center text-error">{t('error')}</p>}>
          <Suspense fallback={<PricingCardsSkeleton />}>
            <div className="relative">
              {/* Enhanced popular plan highlight */}
              <div className="absolute top-0 inset-x-0 h-[600px] bg-gradient-to-b from-primary/5 via-secondary/5 to-transparent -z-10 rounded-[40px]" />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
                {pricingType === 'subscriptions' ? (
                  displayedPlans.map((plan, index) => {
                    const planFeatures = generateFeatures(plan.id, plan.credits_included);
                    const isFeatured = plan.id.toLowerCase().includes('standard') || plan.is_featured;

                    return (
                      <div key={index}
                        className={cn(
                          "transform transition-all duration-500",
                          "hover:scale-105 hover:z-10",
                          "animate-fade-in-up",
                          isFeatured && "lg:-mt-4"
                        )}
                        style={{
                          animationDelay: `${index * 150}ms`
                        }}>
                        <PricingCard
                          plan={{
                            name: plan.name,
                            description: getPlanDesc(plan.id) || plan.description || '',
                            price: plan.price,
                            isFeatured: isFeatured,
                            features: planFeatures,
                            priceId: plan.stripe_price_id || '',
                            variantId: plan.lemonsqueezy_variant_id || '',
                            billingCycle: billingCycle,
                            mode: 'subscription'
                          }}
                        />
                      </div>
                    );
                  })
                ) : (
                  packages.map((pkg, index) => (
                    <div key={index}
                      className={cn(
                        "transform transition-all duration-500",
                        "hover:scale-105 hover:z-10",
                        "animate-fade-in-up"
                      )}
                      style={{
                        animationDelay: `${index * 150}ms`
                      }}>
                      <PricingCard
                        plan={{
                          name: tCard(`packages.${pkg.name}`) || pkg.name,
                          description: pkg.description || '',
                          price: pkg.price,
                          isFeatured: pkg.is_popular || false,
                          features: [
                            { name: tCard('features.oneTimeCredits', { credits: pkg.credits_amount }) },
                            { name: tCard('features.noExpiration') },
                            // { name: tCard('features.priorityProcessing') },
                            { name: tCard('features.allFeatures') }
                          ],
                          priceId: pkg.stripe_price_id || '',
                          variantId: pkg.lemonsqueezy_variant_id || '',
                          billingCycle: 'one-time',
                          mode: 'payment'
                        }}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          </Suspense>
        </ErrorBoundary>

        {unitCosts.isFallback && (
          <p
            role="status"
            className="mx-auto mt-8 max-w-4xl rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-950"
          >
            {productionCopy.fallbackNote}
          </p>
        )}

        {pricingType === 'subscriptions' && displayedPlans.length > 0 && (
          <section className="mx-auto mt-24 max-w-6xl" aria-labelledby="estimated-output-title">
            <div className="max-w-3xl">
              <h2 id="estimated-output-title" className="text-3xl font-bold">
                {productionCopy.tableTitle}
              </h2>
              <p className="mt-4 text-lg leading-8 text-base-content/60">
                {productionCopy.tableDescription}
              </p>
            </div>
            <div className="mt-8 overflow-x-auto rounded-2xl border border-base-200 bg-base-100">
              <table className="w-full min-w-[760px] text-left">
                <thead className="bg-base-200/50 text-sm">
                  <tr>
                    <th className="px-5 py-4 font-semibold">{productionCopy.profileLabel}</th>
                    <th className="px-5 py-4 font-semibold">{productionCopy.creditsLabel}</th>
                    {displayedPlans.map((plan) => (
                      <th key={plan.id} className="px-5 py-4 font-semibold">
                        {plan.name}
                        <span className="mt-1 block text-xs font-normal text-base-content/60">
                          {plan.credits_included.toLocaleString()} {productionCopy.creditsUnit}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {profiles.map((profile) => (
                    <tr key={profile.key} className="border-t border-base-200">
                      <th className="px-5 py-4 text-sm font-semibold">
                        {productionCopy.profiles[profile.key]}
                        <span className="mt-1 block text-xs font-normal text-base-content/60">
                          {profile.durationSeconds / 60} {productionCopy.minutesShort} · {profile.imageCount} {productionCopy.visualsLabel}
                        </span>
                      </th>
                      <td className="px-5 py-4 text-sm text-base-content/70">
                        {profile.minimumCredits.toLocaleString()}-{profile.maximumCredits.toLocaleString()}
                      </td>
                      {displayedPlans.map((plan) => {
                        const output = estimatePlanVideoOutput(plan.credits_included, profile);
                        const formatted = output.maximum < 1
                          ? '<1'
                          : output.minimum < 1
                            ? `<1-${output.maximum}`
                            : output.minimum === output.maximum
                              ? String(output.minimum)
                              : `${output.minimum}-${output.maximum}`;
                        return (
                          <td key={plan.id} className="px-5 py-4 text-sm font-semibold text-primary">
                            {formatted} {productionCopy.outputLabel}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm leading-6 text-base-content/60">
              {productionCopy.estimateNote}
            </p>
          </section>
        )}

        {/* Unit cost detailed Section */}
        <div className="mt-24 max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">{tPage('unitCosts.title')}</h2>
            <p className="text-lg text-base-content/60 max-w-2xl mx-auto">
              {tPage('unitCosts.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
            {/* Text Generation */}
            <div className="bg-base-100/50 backdrop-blur-xl border border-base-200/50 rounded-3xl p-6 hover:shadow-xl hover:scale-105 transition-all duration-300">
              <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center mb-5 text-primary">
                <Type className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold mb-2">{tPage('unitCosts.textGeneration')}</h3>
              <div className="flex items-end gap-1 mb-4 h-10">
                <span className="text-3xl font-extrabold text-primary">
                  {unitCosts.scriptGeneration}
                </span>
                <span className="text-base-content/60 text-sm mb-1 font-medium">{tPage('unitCosts.creditsPerTask')}</span>
              </div>
              <p className="text-base-content/60 text-sm leading-relaxed">
                {tPage('unitCosts.textGenerationDesc')}
              </p>
            </div>

            {/* Image Generation */}
            <div className="bg-base-100/50 backdrop-blur-xl border border-base-200/50 rounded-3xl p-6 hover:shadow-xl hover:scale-105 transition-all duration-300">
              <div className="w-12 h-12 bg-secondary/10 rounded-2xl flex items-center justify-center mb-5 text-secondary">
                <ImageIcon className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold mb-2">{tPage('unitCosts.imageGeneration')}</h3>
              <div className="flex items-end gap-1 mb-4 h-10">
                <span className="text-3xl font-extrabold text-secondary">
                  {unitCosts.imageGeneration}
                </span>
                <span className="text-base-content/60 text-sm mb-1 font-medium">{tPage('unitCosts.creditsPerImage')}</span>
              </div>
              <p className="text-base-content/60 text-sm leading-relaxed">
                {tPage('unitCosts.imageGenerationDesc')}
              </p>
            </div>

            {/* Audio Generation */}
            <div className="bg-base-100/50 backdrop-blur-xl border border-base-200/50 rounded-3xl p-6 hover:shadow-xl hover:scale-105 transition-all duration-300">
              <div className="w-12 h-12 bg-accent/10 rounded-2xl flex items-center justify-center mb-5 text-accent">
                <Mic className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold mb-2">{tPage('unitCosts.audioGeneration')}</h3>
              <div className="flex flex-col mb-4 h-10 justify-end">
                <div className="flex items-end gap-1">
                  <span className="text-3xl font-extrabold text-accent">
                    {unitCosts.audioGenerationMinimum}~{unitCosts.audioGenerationMaximum}
                  </span>
                  <span className="text-base-content/60 text-sm mb-1 font-medium">{tPage('unitCosts.creditsPerSecond')}</span>
                </div>
              </div>
              <p className="text-base-content/60 text-sm leading-relaxed">
                {tPage('unitCosts.audioGenerationDesc')}
              </p>
            </div>

            {/* Video Synthesis */}
            <div className="bg-base-100/50 backdrop-blur-xl border border-base-200/50 rounded-3xl p-6 hover:shadow-xl hover:scale-105 transition-all duration-300">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-2xl flex items-center justify-center mb-5 text-emerald-500">
                <Video className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold mb-2">{tPage('unitCosts.videoSynthesis')}</h3>
              <div className="flex items-end gap-1 mb-4 h-10">
                <span className="text-3xl font-extrabold text-emerald-500">
                  {unitCosts.videoComposition}
                </span>
                <span className="text-base-content/60 text-sm mb-1 font-medium">{tPage('unitCosts.creditsPerSecond')}</span>
              </div>
              <p className="text-base-content/60 text-sm leading-relaxed">
                {tPage('unitCosts.videoSynthesisDesc')}
              </p>
            </div>
          </div>
        </div>

        {/* All Plans Include Section */}
        <div className="mt-24 text-center">
          <h2 className="text-3xl font-bold mb-4">{tPage('allPlansInclude.title')}</h2>
          <p className="text-lg text-base-content/60 max-w-2xl mx-auto mb-12">
            {tPage('allPlansInclude.subtitle')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 max-w-4xl mx-auto text-left">
            {tPage.raw('allPlansInclude.features').map((feature: string, index: number) => (
              <div key={index} className="flex items-center gap-3">
                <div className="bg-green-100 dark:bg-green-900/50 p-1.5 rounded-full">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <span className="font-medium">{feature}</span>
              </div>
            ))}
          </div>
        </div>


        {/* Custom Enterprise Solutions */}
        <div className="mt-24 text-center">
          <h2 className="text-3xl font-bold mb-4">{tPage('customSolution.title')}</h2>
          <p className="text-lg text-base-content/60 max-w-2xl mx-auto mb-8">
            {tPage('customSolution.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <div className="flex items-center gap-2 text-base-content/80">
              <span className="text-sm font-medium">{tPage('customSolution.contactTeam')}</span>
              <a
                href="mailto:support@ariaflow.ai"
                className="text-primary font-semibold hover:text-primary/80 transition-colors duration-200"
              >
                support@ariaflow.ai
              </a>
            </div>
            <div className="hidden sm:block w-px h-6 bg-base-content/20"></div>
            <div className="text-sm text-base-content/60">
              {tPage('customSolution.responseTime')}
            </div>
          </div>
        </div>

        {/* Enhanced trust badges */}
        <div className="mt-24 text-center">
          <p className="text-sm font-medium text-base-content/60 mb-8">
            {tPage('trustBadges.title')}
          </p>
          <div className="flex flex-wrap justify-center items-center gap-12">
            <img src="/images/youtube-red.svg" alt="YouTube"
              className="h-8 opacity-60 hover:opacity-100 transition-opacity duration-300" />
            <img src="/images/tiktok.svg" alt="TikTok"
              className="h-8 opacity-60 hover:opacity-100 transition-opacity duration-300" />
            <img src="/images/instagram.svg" alt="Instagram"
              className="h-8 opacity-60 hover:opacity-100 transition-opacity duration-300" />
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingCardsSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
      {[...Array(3)].map((_, index) => (
        <div
          key={index}
          className="animate-pulse bg-base-100 rounded-3xl shadow-xl p-8 border border-base-200/50 backdrop-blur-xl"
        >
          <div className="space-y-6">
            <div className="h-8 bg-base-200 rounded-full w-3/4"></div>
            <div className="h-4 bg-base-200 rounded-full w-full"></div>
            <div className="h-4 bg-base-200 rounded-full w-5/6"></div>
            <div className="h-14 bg-base-200 rounded-full w-2/3"></div>
            <div className="pt-6 space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-4 bg-base-200 rounded-full w-full"></div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
